import {
  Forum,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@styled-icons/material';
import { useState } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';

import { Button, Input, Textarea } from '~/components/Elements';
import { selectUser } from '~/containers/Auth/selectors';
import { colors, spacing } from '~/utils/css-mixins';
import media from '~/utils/media';
import { apiUrl, request } from '~/utils/request';

const mapStateToProps = createStructuredSelector({
  user: selectUser,
});

const Wrapper = styled.div`
  margin: ${spacing.siteGutter * 2}px 0;
`;

const Label = styled.div`
  margin-bottom: 5px;
  color: ${colors.textmeta};
  font-size: 14px;
  line-height: 22px;
`;

const QuestionBoxToggle = styled.button`
  border: solid 1px ${colors.border};
  padding: ${spacing.siteGutter}px 52px;
  width: 100%;
  position: relative;
  background-color: ${colors.bgsitecomplementary};
  font-size: 14px;
  line-height: 22px;
  text-align: left;
  appearance: none;

  svg {
    width: 24px;
    height: 24px;
    position: absolute;
    top: 12px;
    fill: ${colors.bluedark};

    ${media.tablet`
      top: 50%;
      transform: translateY(-50%);
    `}
  }

  svg:first-child {
    left: ${spacing.siteGutter}px;
  }

  svg:last-child {
    width: 30px;
    height: 30px;
    right: ${spacing.siteGutter}px;
    fill: ${colors.textmeta};
  }

  :focus {
    outline: none;
  }
`;

const QuestionBox = styled.div`
  border: solid 1px ${colors.border};
  padding: ${spacing.siteGutter}px;
  background-color: ${colors.bgsitecomplementary};

  form > * + * {
    margin-top: ${spacing.siteGutter}px;
  }

  textarea {
    resize: vertical;
  }
`;

const Message = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: ${props =>
    props.status === 'fail' ? colors.textfail : colors.textsuccess};
`;

function QuestionForm({ user, round }) {
  // const initialName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  const initialName = 'Anonym';
  const userEmail = user.email || 'tipsgruppen@travronden.se';

  const [userName, setUserName] = useState(initialName);
  const [userQuestion, setUserQuestion] = useState('');
  const [questionFormOpen, setQuestionFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const sendQuestion = async () => {
    const { id: round_id } = round;

    const question = userQuestion.trim();

    if (!question) {
      setMessage({
        type: 'fail',
        text: 'Namn är frivilligt att ange men du måste skriva en fråga!',
      });
      setIsSubmitting(false);
      return;
    }

    /** Send the email with the question */
    const response = await request(apiUrl('content/live/question'), {
      method: 'POST',
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
      body: JSON.stringify({
        round_id,
        email: userEmail,
        name: userName,
        body: userQuestion,
        receiver: 'tipsgruppen@travronden.se',
      }),
    });

    /** Close the form and set user message */
    let userMessage = {
      type: 'success',
      text: 'Tack för din fråga! Vi kommer att försöka besvara denna i vår kommande liverapportering.',
    };

    if (response !== 'Success') {
      userMessage = {
        type: 'fail',
        text: 'Det gick inte att skicka din fråga. Vänligen försök igen.',
      };
    }

    setMessage(userMessage);

    /** Reset the form values */
    setQuestionFormOpen(false);
    setUserName('');
    setUserQuestion('');
  };

  const toggleQuestionBox = () => {
    setQuestionFormOpen(!questionFormOpen);
    setMessage(null);
  };

  const handleSubmit = evn => {
    evn.preventDefault();
    setIsSubmitting(true);
    sendQuestion();
  };

  return (
    <Wrapper>
      <QuestionBoxToggle onClick={() => toggleQuestionBox()}>
        <Forum />
        <b>Har du en fråga till våra tipsters?</b> Ställ en fråga så försöker vi
        besvara den.
        {questionFormOpen ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
      </QuestionBoxToggle>

      {questionFormOpen && (
        <QuestionBox>
          <form onSubmit={handleSubmit}>
            <div>
              <Label>Namn (ej obligatoriskt)</Label>
              <Input
                type="text"
                value={userName}
                placeholder="Ditt namn"
                onChange={evn => setUserName(evn.target.value)}
              />
            </div>

            <Textarea
              rows={4}
              onChange={evn => setUserQuestion(evn.target.value)}
              placeholder="Din fråga"
              value={userQuestion}
            />

            <Button
              type="submit"
              disabled={isSubmitting}
              as="button"
              size="large"
            >
              Skicka din fråga
            </Button>
          </form>
        </QuestionBox>
      )}

      {message && (
        <QuestionBox>
          <Message status={message.type}>{message.text}</Message>
        </QuestionBox>
      )}
    </Wrapper>
  );
}

export default connect(mapStateToProps, null)(QuestionForm);
